import { BBAnchor, Box } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { forwardRef, Ref, useCallback, useMemo, useState } from 'react';
import { Vector3, Vector3Tuple } from 'three';

const anchor = [0, 0, 0] as const;

const middleAnchor: [number, number, number] = [0, 0, 0];

export interface FrameMiddleProps {
  length: number;
  ctx?: any;
}

export interface FrameCapProps {
  onSizeChange: (size: Vector3) => void;
  ctx?: any;
}

export interface FrameLayoutProps {
  axis?: 'x' | 'y' | 'z';
  debug?: boolean;
  width: number;
  height: number;
  depth: number;
  StartCap: React.FunctionComponent<FrameCapProps>;
  MiddleCap: React.FunctionComponent<FrameMiddleProps>;
  EndCap: React.FunctionComponent<FrameCapProps>;
  startOffset?: number;
  endOffset?: number;
  ctx?: any;
}

export const FrameLayout = forwardRef(
  (
    {
      axis = 'x',
      debug,
      width,
      height,
      depth,
      MiddleCap,
      StartCap,
      EndCap,
      startOffset = 0,
      endOffset = 0,
      ctx,
    }: FrameLayoutProps,
    ref: Ref<any>
  ) => {
    const [startCapLength, setStartCapLength] = useState(0);
    const [endCapLength, setEndCapLength] = useState(0);

    const startLength = startCapLength + startOffset;
    const endLength = endCapLength + endOffset;

    const { invalidate } = useThree();

    const handleStartSizeChange = useCallback(
      (size: Vector3) => {
        const sizeAlongAxis = size[axis];
        if (sizeAlongAxis !== startCapLength) {
          setStartCapLength(sizeAlongAxis);
          invalidate();
        }
      },
      [setStartCapLength, axis, startCapLength, invalidate]
    );

    const handleEndSizeChange = useCallback(
      (size: Vector3) => {
        const sizeAlongAxis = size[axis];
        if (sizeAlongAxis !== endCapLength) {
          setEndCapLength(sizeAlongAxis);
          invalidate();
        }
      },
      [setEndCapLength, axis, endCapLength, invalidate]
    );

    const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
    const startAnchor = useMemo(
      () => anchor.map((a, i) => (i === axisIndex ? 1 : a)) as Vector3Tuple,
      [axisIndex]
    );

    const endAnchor = useMemo(
      () => anchor.map((a, i) => (i === axisIndex ? -1 : a)) as Vector3Tuple,
      [axisIndex]
    );

    const startBoxPosition = useMemo(
      () =>
        anchor.map((a, i) =>
          i === axisIndex ? -startCapLength / 2.0 : a
        ) as Vector3Tuple,
      [startCapLength, axisIndex]
    );

    const endBoxPosition = useMemo(
      () =>
        anchor.map((a, i) =>
          i === axisIndex ? endCapLength / 2.0 : a
        ) as Vector3Tuple,
      [endCapLength, axisIndex]
    );

    const middleBoxPosition = useMemo(
      () =>
        anchor.map((a, i) =>
          i === axisIndex ? -startLength / 2.0 + endLength / 2 : a
        ) as Vector3Tuple,
      [startLength, endLength, axisIndex]
    );

    const dimensions = useMemo(
      () => [width, height, depth],
      [width, height, depth]
    );

    const startBoxArgs = useMemo(
      () =>
        dimensions.map((d, i) =>
          i === axisIndex ? startCapLength : d
        ) as Vector3Tuple,
      [dimensions, startCapLength, axisIndex]
    );

    const endBoxArgs = useMemo(
      () =>
        dimensions.map((d, i) =>
          i === axisIndex ? endCapLength : d
        ) as Vector3Tuple,
      [dimensions, endCapLength, axisIndex]
    );

    const middleBoxArgs = useMemo(
      () =>
        dimensions.map((d, i) =>
          i === axisIndex ? dimensions[axisIndex] - startLength - endLength : d
        ) as Vector3Tuple,
      [dimensions, axisIndex, startLength, endLength]
    );

    const length = dimensions[axisIndex] - startLength - endLength;

    return (
      <Box
        ref={ref}
        visible={false}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        args={dimensions}
      >
        <BBAnchor anchor={startAnchor}>
          <Box position={startBoxPosition} args={startBoxArgs}>
            <meshStandardMaterial
              visible={debug}
              color="red"
              wireframe={true}
            />
            <StartCap onSizeChange={handleStartSizeChange} ctx={ctx} />
          </Box>
        </BBAnchor>
        <BBAnchor anchor={middleAnchor}>
          <Box args={middleBoxArgs} position={middleBoxPosition}>
            <meshStandardMaterial
              visible={debug}
              color="yellow"
              wireframe={true}
            />
            <MiddleCap length={Math.abs(length)} ctx={ctx} />
          </Box>
        </BBAnchor>
        <BBAnchor anchor={endAnchor}>
          <Box position={endBoxPosition} args={endBoxArgs}>
            <meshStandardMaterial
              visible={debug}
              color="green"
              wireframe={true}
            />
            <EndCap onSizeChange={handleEndSizeChange} ctx={ctx} />
          </Box>
        </BBAnchor>
      </Box>
    );
  }
);
